import { useEffect, useMemo, useRef, useState } from "react";

import { stepCompletion } from "@domain/steps.js";
import type { WorkspaceView } from "@domain/workspace-view.js";

import {
  answerClarification,
  cancelClarificationGeneration,
  continueFromClarify,
  fetchWorkspace,
  generateClarifications,
  skipClarification,
} from "./api.js";

export interface ClarifyStepProps {
  readonly tenantId: string;
  readonly view: WorkspaceView;
  readonly onViewChanged: (view: WorkspaceView) => void;
  readonly onContinued: (cycle: WorkspaceView["cycle"]) => void;
}

/** Focused, explicit choices rather than a requirements questionnaire. */
export const ClarifyStep = ({
  tenantId,
  view,
  onViewChanged,
  onContinued,
}: ClarifyStepProps): React.JSX.Element => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [generatingId, setGeneratingId] = useState<string | undefined>();
  const [savingId, setSavingId] = useState<string | undefined>();
  const [continuing, setContinuing] = useState(false);
  const [notice, setNotice] = useState<string | undefined>();
  const [failure, setFailure] = useState<string | undefined>();
  const onViewChangedRef = useRef(onViewChanged);
  onViewChangedRef.current = onViewChanged;

  const answers = useMemo(
    () => new Map(view.answers.map((answer) => [answer.questionId, answer] as const)),
    [view.answers],
  );
  const completion = stepCompletion("clarify", view.governed.clarify ?? []);
  const remoteGeneration = view.cycle.clarificationGeneration;
  const activeGenerationId =
    generatingId ??
    (remoteGeneration?.status === "running" ? remoteGeneration.id : undefined);

  useEffect(() => {
    setDrafts((current) => {
      const next = { ...current };
      for (const question of view.questions) {
        const answer = answers.get(question.id);
        if (next[question.id] === undefined) {
          next[question.id] = answer?.outcome === "answered" ? answer.text : "";
        }
      }
      return next;
    });
  }, [answers, view.questions]);

  useEffect(() => {
    if (!activeGenerationId) return;
    let stopped = false;
    const deadline = Date.now() + 360_000;

    const poll = async (): Promise<void> => {
      while (!stopped && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 1_500));
        if (stopped) return;
        try {
          const updated = await fetchWorkspace(tenantId);
          if (stopped) return;
          onViewChangedRef.current(updated);
          const generation = updated.cycle.clarificationGeneration;
          if (generation?.id !== activeGenerationId) {
            setGeneratingId(undefined);
            setFailure("The generation request changed. Reload before trying again.");
            return;
          }
          if (generation.status === "completed") {
            setGeneratingId(undefined);
            setFailure(undefined);
            setNotice(
              updated.questions.length === 0
                ? "No material questions were needed."
                : `Generated ${updated.questions.length} focused question${
                    updated.questions.length === 1 ? "" : "s"
                  }.`,
            );
            return;
          }
          if (generation.status === "failed") {
            setGeneratingId(undefined);
            setNotice(undefined);
            setFailure(undefined);
            return;
          }
          if (generation.status === "cancelled") {
            setGeneratingId(undefined);
            setFailure(undefined);
            setNotice("Generation cancelled. The previous questions were kept.");
            return;
          }
          setFailure(undefined);
        } catch (error) {
          setFailure(
            `Status check failed; polling will retry. ${
              error instanceof Error ? error.message : String(error)
            }`,
          );
        }
      }
      if (!stopped) {
        setFailure(
          "Generation is taking longer than expected. Reload to resume status checks or cancel it.",
        );
      }
    };

    void poll();
    return () => {
      stopped = true;
    };
  }, [activeGenerationId, tenantId]);

  const generate = async (): Promise<void> => {
    if (generatingId) return;
    const requestId = crypto.randomUUID();
    setGeneratingId(requestId);
    setFailure(undefined);
    setNotice(undefined);
    try {
      const generated = await generateClarifications(tenantId, requestId);
      onViewChanged(generated.view);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
      setGeneratingId((current) => (current === requestId ? undefined : current));
    }
  };

  const cancel = async (): Promise<void> => {
    if (!activeGenerationId) return;
    const requestId = activeGenerationId;
    setFailure(undefined);
    try {
      const cycle = await cancelClarificationGeneration(tenantId, requestId);
      onViewChanged({ ...view, cycle });
      setNotice("Generation cancelled. The previous questions were kept.");
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
    } finally {
      setGeneratingId((current) => (current === requestId ? undefined : current));
    }
  };

  const save = async (
    questionId: string,
    outcome: "answered" | "skipped",
  ): Promise<void> => {
    if (savingId) return;
    setSavingId(questionId);
    setFailure(undefined);
    setNotice(undefined);
    try {
      const updated =
        outcome === "answered"
          ? await answerClarification(
              tenantId,
              questionId,
              drafts[questionId] ?? "",
              crypto.randomUUID(),
            )
          : await skipClarification(tenantId, questionId, crypto.randomUUID());
      onViewChanged(updated);
      setNotice(outcome === "answered" ? "Answer saved." : "Question skipped.");
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
    } finally {
      setSavingId(undefined);
    }
  };

  const continueNow = async (): Promise<void> => {
    if (!completion.complete || continuing) return;
    setContinuing(true);
    setFailure(undefined);
    try {
      onContinued(await continueFromClarify(tenantId));
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
    } finally {
      setContinuing(false);
    }
  };

  const terminalGeneration = view.cycle.clarificationGeneration;

  return (
    <section aria-labelledby="clarify-heading" className="panel clarify">
      <h2 id="clarify-heading">Clarify the important choices</h2>
      <p>
        Generate a short set of questions. Answer each one or skip it with the
        stated assumption—nothing is decided silently.
      </p>

      {view.clarificationOutdated && (
        <p className="blocked" role="status">
          The description changed. Regenerate the questions; answers whose
          subject still matches will be preserved.
        </p>
      )}

      {activeGenerationId ? (
        <div className="generation-status" aria-live="polite">
          <span aria-busy="true">Generating focused questions…</span>
          <button type="button" onClick={() => void cancel()}>
            Cancel generation
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => void generate()}>
          {view.cycle.clarificationSetId ? "Regenerate questions" : "Generate questions"}
        </button>
      )}

      {terminalGeneration?.status === "failed" && terminalGeneration.message && (
        <p role="alert">{terminalGeneration.message}</p>
      )}
      {notice && <p role="status">{notice}</p>}
      {failure && <p role="alert">{failure}</p>}

      {view.cycle.clarificationSetId && view.questions.length === 0 && (
        <p>No material questions were needed for this description.</p>
      )}

      <ol className="clarification-list">
        {view.questions.map((question) => {
          const answer = answers.get(question.id);
          const draft = drafts[question.id] ?? "";
          const busy = savingId === question.id;
          return (
            <li key={question.id} className="clarification-card">
              <h3>{question.text}</h3>
              <p className="question-why">Why this matters: {question.why}</p>
              <label htmlFor={`answer-${question.id}`}>Your answer</label>
              <textarea
                id={`answer-${question.id}`}
                rows={3}
                value={draft}
                onChange={(event) =>
                  setDrafts((current) => ({
                    ...current,
                    [question.id]: event.target.value,
                  }))
                }
              />
              <div className="question-actions">
                <button
                  type="button"
                  disabled={draft.trim() === "" || busy}
                  onClick={() => void save(question.id, "answered")}
                >
                  {busy ? "Saving…" : answer?.outcome === "answered" ? "Update answer" : "Save answer"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void save(question.id, "skipped")}
                >
                  Skip
                </button>
              </div>
              {answer?.outcome === "answered" && (
                <p className="settled">Answered</p>
              )}
              {answer?.outcome === "skipped" && (
                <p className="assumption">
                  <strong>Assumption:</strong> {answer.assumption}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      <p className="progress">
        {completion.settled} of {completion.total} clarification choices settled
      </p>
      <button
        type="button"
        className="continue"
        disabled={!completion.complete || continuing}
        onClick={() => void continueNow()}
      >
        {continuing ? "Continuing…" : "Continue to Backlog"}
      </button>
      {!completion.complete && (
        <p className="blocked">
          Generate or regenerate as needed, then answer or skip every question.
        </p>
      )}
    </section>
  );
};
